import { useState } from 'react';
import { Loader2, AlertTriangle, CheckCircle2, XCircle, KeyRound } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';


function PresenceBadge({ present, label }) {
  return (
    <div className="flex items-center gap-2">
      {present ? (
        <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
      ) : (
        <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
      )}
      <span className="text-sm">{label}</span>
      <Badge variant={present ? 'default' : 'destructive'} className="text-[10px]">
        {present ? 'present' : 'absent'}
      </Badge>
    </div>
  );
}


export function XeroCredentialsPanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const runCheck = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await diagnosticsAPI.xeroCredentials();
      setData(d);
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <KeyRound className="h-5 w-5" />
          Xero Credentials
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Read-only check of Xero OAuth credential resolution state.
          Reports presence/absence of env vars, DB connection row, and token state.
          No secret values are exposed.
        </p>

        <Button
          variant="outline"
          size="sm"
          onClick={runCheck}
          disabled={loading}
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
          Check Xero Credentials
        </Button>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        )}

        {data && (
          <div className="space-y-4">
            {/* Verdict */}
            <div className="rounded border px-3 py-2 bg-muted/20">
              <p className="text-xs font-medium text-muted-foreground mb-1">Verdict</p>
              <p className="text-sm font-semibold">{data.verdict}</p>
            </div>

            {/* Resolved credentials (effective source) */}
            {data.resolved_credentials && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">Resolved Credentials (effective)</p>
                <PresenceBadge present={data.resolved_credentials.client_id_present} label="client_id" />
                <PresenceBadge present={data.resolved_credentials.client_secret_present} label="client_secret" />
                <div className="ml-4 text-xs text-muted-foreground">
                  Source: <span className="font-medium">{data.resolved_credentials.source}</span>
                </div>
              </div>
            )}

            {/* Env vars */}
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Environment Variables</p>
              {data.env_vars && Object.values(data.env_vars).map((v) => (
                <PresenceBadge key={v.name} present={v.present} label={v.name} />
              ))}
            </div>

            {/* Redirect URI */}
            {data.redirect_uri !== undefined && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Redirect URI (not a secret)</p>
                <p className="text-sm font-mono bg-muted px-2 py-0.5 rounded">
                  {data.redirect_uri || '(not configured, built dynamically from origin)'}
                </p>
              </div>
            )}

            {/* DB row */}
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">DB Connection Row (xero_connections)</p>
              <PresenceBadge present={data.db_row?.exists} label="Row exists" />
              {data.db_row?.exists && data.db_row.fields && Object.entries(data.db_row.fields).map(([key, field]) => (
                <div key={key} className="ml-4">
                  <PresenceBadge present={field.present} label={key} />
                  {field.value !== undefined && field.value !== null && (
                    <span className="ml-6 text-xs text-muted-foreground">
                      = {String(field.value)}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Requested scopes */}
            {data.requested_scopes !== undefined && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">Requested Scopes</p>
                <p className="text-xs font-mono bg-muted px-2 py-1 rounded break-all">
                  {data.requested_scopes}
                </p>
                <div className="flex items-center gap-2">
                  {data.contains_broad_scope ? (
                    <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                  ) : (
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
                  )}
                  <span className="text-sm">contains_broad_scope</span>
                  <Badge variant={data.contains_broad_scope ? 'destructive' : 'default'} className="text-[10px]">
                    {data.contains_broad_scope ? 'true (will fail on granular-only apps)' : 'false'}
                  </Badge>
                </div>
              </div>
            )}

            {/* Token state */}
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Token State</p>
              <PresenceBadge present={data.token?.present} label="Access token" />
              {data.token?.present && data.token.expires_at && (
                <div className="ml-4 text-xs text-muted-foreground space-y-0.5">
                  <p>Expires at: {new Date(data.token.expires_at * 1000).toLocaleString()}</p>
                  <p>
                    Expired:{' '}
                    <span className={data.token.expired ? 'text-red-600 font-medium' : 'text-green-600 font-medium'}>
                      {data.token.expired ? 'yes' : 'no'}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
